import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE } from './api';
import { formatDateTime, formatDateTimeRange } from './formatDate';
import FormattedText from './FormattedText';

const REJECTION_REASONS = [
  'Missing or unclear event details',
  'Location needs clarification',
  'Potentially misleading or unsafe content',
  'Other',
];

function getWarnings(event) {
  const content = `${event.title || ''} ${event.description || ''} ${event.location || ''}`;
  const warnings = [];
  if (/(https?:\/\/|www\.)/i.test(content)) warnings.push('Contains a link');
  if (/(venmo|paypal|crypto|bitcoin|\bbtc\b|wallet|payment|send money|dm for details)/i.test(content)) {
    warnings.push('Mentions payment, wallet, or “DM for details”');
  }
  if (!event.location || event.location.trim().length < 5 || /^(tba|tbd|somewhere|secret|unknown)$/i.test(event.location.trim())) {
    warnings.push('Location may be too vague');
  }
  return warnings;
}

function requestHeaders(token, json = false) {
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    Authorization: `Bearer ${token}`,
  };
}

function PendingEventCard({ event, token, onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState(REJECTION_REASONS[0]);
  const [customReason, setCustomReason] = useState('');
  const warnings = getWarnings(event);

  const decide = async (action, body) => {
    setBusy(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/admin/events/${encodeURIComponent(event.id)}/${action}`, {
        method: 'PATCH',
        headers: requestHeaders(token, Boolean(body)),
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Moderation action failed');
      onDone(event.id);
    } catch (actionError) {
      setError(actionError.message || 'Moderation action failed');
      setBusy(false);
    }
  };

  const confirmReject = () => {
    const finalReason = reason === 'Other' ? customReason.trim() : reason;
    if (!finalReason) {
      setError('Enter a reason before rejecting.');
      return;
    }
    decide('reject', { reason: finalReason });
  };

  return (
    <article className="moderation-card" aria-label={event.title}>
      {event.bannerImage && <img className="moderation-card-banner" src={event.bannerImage} alt="" />}
      <div className="moderation-card-body">
        <h2>{event.title}</h2>
        <p className="moderation-event-facts">
          {[formatDateTimeRange(event.dateTime, event.endDateTime) || [event.date, event.time].filter(Boolean).join(' ✶ '), event.location || 'No location'].filter(Boolean).join(' ✶ ')}
        </p>
        {event.description
          ? <FormattedText className="moderation-description" text={event.description} />
          : <p className="moderation-description">No description provided.</p>}
        <p className="moderation-meta">
          {event.isPublic ? 'Public' : 'Private'} ✶ Submitted {event.createdAt ? formatDateTime(event.createdAt) : 'unknown'}
        </p>

        {warnings.length > 0 && (
          <ul className="moderation-warning">
            {warnings.map((warning) => <li key={warning}>⚠ {warning}</li>)}
          </ul>
        )}

        {error && <div className="moderation-error" role="alert">{error}</div>}

        {rejecting ? (
          <div className="moderation-reject-form">
            <label>Reason
              <select value={reason} onChange={(changeEvent) => setReason(changeEvent.target.value)}>
                {REJECTION_REASONS.map((item) => <option key={item}>{item}</option>)}
              </select>
            </label>
            {reason === 'Other' && (
              <textarea
                value={customReason}
                onChange={(changeEvent) => setCustomReason(changeEvent.target.value)}
                placeholder="Write a reason the creator will see"
                maxLength={500}
                rows={3}
              />
            )}
            <div className="moderation-actions">
              <button type="button" className="moderation-danger" disabled={busy} onClick={confirmReject}>Confirm reject</button>
              <button type="button" className="moderation-secondary" disabled={busy} onClick={() => setRejecting(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="moderation-actions">
            <button type="button" className="moderation-accept" disabled={busy} onClick={() => decide('approve')}>Accept</button>
            <button type="button" className="moderation-danger" disabled={busy} onClick={() => setRejecting(true)}>Reject</button>
          </div>
        )}
      </div>
    </article>
  );
}

// Open reports from the Report button (EU Digital Services Act notice and action).
function ReportsSection({ token }) {
  const [reports, setReports] = useState([]);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');

  const loadReports = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/reports`, { headers: requestHeaders(token) });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Could not load reports');
      setReports(Array.isArray(data.reports) ? data.reports : []);
    } catch (loadError) {
      setError(loadError.message || 'Could not load reports');
    }
  }, [token]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const resolve = async (report, action) => {
    if (action === 'remove' && !window.confirm(`Remove “${report.eventTitle}”? It will be hidden and its creator will see: Removed after a report: ${report.reason}`)) {
      return;
    }
    setBusyId(report.id);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/admin/reports/${encodeURIComponent(report.id)}/resolve`, {
        method: 'PATCH',
        headers: requestHeaders(token, true),
        body: JSON.stringify({ action }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Could not update the report');
      await loadReports();
    } catch (resolveError) {
      setError(resolveError.message || 'Could not update the report');
    } finally {
      setBusyId('');
    }
  };

  if (!reports.length && !error) return null;

  return (
    <section className="moderation-reports" aria-label="Reports">
      <h2>Reports ({reports.length})</h2>
      {error && <div className="moderation-error" role="alert">{error}</div>}
      {reports.map((report) => (
        <article key={report.id} className="moderation-report">
          <div>
            <a href={`/event/${encodeURIComponent(report.eventSlug)}`} target="_blank" rel="noopener noreferrer">
              <strong>{report.eventTitle}</strong>
            </a>
            <span className="moderation-meta"> ✶ {report.eventStatus} ✶ reported {formatDateTime(report.createdAt)}</span>
          </div>
          <p className="moderation-report-reason">{report.reason}</p>
          {report.details && <p className="moderation-description">{report.details}</p>}
          <div className="moderation-actions">
            {report.eventStatus !== 'deleted' && report.eventStatus !== 'rejected' && (
              <button type="button" className="moderation-danger" disabled={busyId === report.id} onClick={() => resolve(report, 'remove')}>
                Remove event
              </button>
            )}
            <button type="button" className="moderation-secondary" disabled={busyId === report.id} onClick={() => resolve(report, 'dismiss')}>
              Dismiss
            </button>
          </div>
        </article>
      ))}
    </section>
  );
}

export default function ModerationPanel({ token }) {
  const [access, setAccess] = useState('checking');
  const [events, setEvents] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadPending = useCallback(async (cursor = null) => {
    setLoading(true);
    setError('');
    try {
      const query = new URLSearchParams({ status: 'pending', limit: '20' });
      if (cursor) query.set('cursor', cursor);
      const response = await fetch(`${API_BASE}/api/admin/events?${query.toString()}`, {
        headers: requestHeaders(token),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Could not load pending posts');
      const page = Array.isArray(data.events) ? data.events : [];
      setEvents((previous) => (cursor ? [...previous, ...page] : page));
      setPendingCount(data.counts?.pending || 0);
      setNextCursor(data.nextCursor || null);
    } catch (loadError) {
      setError(loadError.message || 'Could not load pending posts');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    async function verifyAdmin() {
      if (!token) {
        setAccess('denied');
        return;
      }
      try {
        const response = await fetch(`${API_BASE}/api/admin/me`, {
          headers: requestHeaders(token),
        });
        if (!cancelled) setAccess(response.ok ? 'admin' : 'denied');
      } catch (verifyError) {
        if (!cancelled) setAccess('denied');
      }
    }
    verifyAdmin();
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (access === 'admin') loadPending();
  }, [access, loadPending]);

  const removeDecided = (id) => {
    setEvents((previous) => previous.filter((event) => event.id !== id));
    setPendingCount((count) => Math.max(0, count - 1));
  };

  if (access === 'checking') {
    return <div className="moderation-state">Checking moderator access…</div>;
  }
  if (access !== 'admin') {
    return <div className="moderation-state"><h1>404</h1><p>Page not found.</p></div>;
  }

  return (
    <main className="moderation-page">
      <header className="moderation-heading">
        <div>
          <p className="moderation-kicker">FROLIK / MODERATION</p>
          <h1>Pending posts ({pendingCount})</h1>
        </div>
        <button type="button" className="moderation-secondary" disabled={loading} onClick={() => loadPending()}>Refresh</button>
      </header>

      <ReportsSection token={token} />

      {error && <div className="moderation-error" role="alert">{error}</div>}

      <section className="moderation-queue" aria-label="Pending posts">
        {events.map((event) => (
          <PendingEventCard key={event.id} event={event} token={token} onDone={removeDecided} />
        ))}
        {!loading && !events.length && <p className="moderation-state">Nothing waiting for review.</p>}
        {loading && <p className="moderation-state">Loading…</p>}
        {nextCursor && !loading && (
          <button type="button" className="moderation-secondary" onClick={() => loadPending(nextCursor)}>Load more</button>
        )}
      </section>
    </main>
  );
}
